import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Apple, Play, CheckCircle2, Flag } from "lucide-react";
import Reveal from "@/components/landing/Reveal";
import { renderTitle } from "@/components/landing/utils";



const MILESTONES = ["Local firmado", "Acondicionamiento", "Inventario recibido", "Apertura"];

function DashboardScreen({ cycle }) {
  const points = [8, 14, 12, 22, 27, 33, 31, 42, 48, 55, 61, 70];
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${(i / (points.length - 1)) * 100},${60 - p * 0.8}`).join(" ");
  return (
    <div className="w-full h-full bg-[#0A0A0B] text-white flex flex-col px-[7%] pt-[14%] pb-[6%] font-sans select-none text-[clamp(7px,2.6vw,10px)] sm:text-[clamp(7px,0.75vw,10px)]">
      <div className="flex justify-between text-[0.9em] text-white/50 font-mono"><span>9:41</span><span>SocioRubí</span><span>●●●</span></div>
      <div className="mt-6 text-[0.8em] uppercase tracking-[0.2em] text-[#FFBF57] font-mono">Utilidad del mes</div>
      <motion.div key={cycle} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-[2.8em] leading-none mt-1 num-mono">$18,450</motion.div>
      <div className="text-[0.9em] text-emerald-400 mt-1">Pago el día 10 · reporte mensual</div>
      <svg viewBox="0 0 100 62" className="mt-4 w-full h-[5.5em] overflow-visible">
        <defs><linearGradient id="g" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#FFBF57" stopOpacity="0.35" /><stop offset="1" stopColor="#FFBF57" stopOpacity="0" /></linearGradient></defs>
        <motion.path key={`a${cycle}`} d={`${path} L100,62 L0,62 Z`} fill="url(#g)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.8 }} />
        <motion.path key={`l${cycle}`} d={path} fill="none" stroke="#FFBF57" strokeWidth="1.6" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, ease: "easeOut" }} />
      </svg>
      <div className="mt-4 rounded-2xl bg-white/[0.06] p-3.5">
        <div className="flex justify-between text-[0.8em] uppercase tracking-widest font-mono text-white/60"><span>Avance de apertura</span><motion.span key={`p${cycle}`} className="text-[#FFBF57]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.4 }}>86 %</motion.span></div>
        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div key={`b${cycle}`} className="h-full bg-[#FFBF57] rounded-full" initial={{ width: "42%" }} animate={{ width: "86%" }} transition={{ delay: 1.2, duration: 1.6, ease: "easeInOut" }} />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {MILESTONES.map((m, i) => (
          <motion.div key={`${m}${cycle}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 2.2 + i * 0.5 }} className="flex items-center gap-2 text-[1em]">
            {i < 3 ? <CheckCircle2 size={13} className="text-[#FFBF57]" /> : <Flag size={13} className="text-white/40" />}
            <span className={i < 3 ? "text-white/90" : "text-white/50"}>{m}</span>
            <span className="ml-auto text-[0.8em] font-mono text-white/40">{i < 3 ? "Listo" : "Próximo"}</span>
          </motion.div>
        ))}
      </div>
      <div className="mt-auto rounded-xl bg-[#FFBF57] text-[#0A0A0A] text-center text-[1em] font-semibold py-[0.8em]">Ver reporte del mes</div>
    </div>
  );
}

function PhoneInHand() {
  const [cycle, setCycle] = useState(0);
  useEffect(() => { const t = setInterval(() => setCycle((c) => c + 1), 9000); return () => clearInterval(t); }, []);
  return (
    <div className="relative w-full aspect-[630/600] rounded-3xl overflow-hidden bg-[#F4F4F5] flex items-center justify-center" data-testid="mobile-app-phone">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="absolute -left-20 top-10 w-72 h-72 rounded-full bg-[#FFBF57]/25 blur-3xl" />
      <div className="absolute right-0 bottom-0 w-64 h-64 rounded-full bg-[#0A0A0A]/10 blur-3xl" />
      <motion.div
        className="relative w-[44%] max-w-[225px]"
        style={{ aspectRatio: "9 / 19.5" }}
        animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="absolute inset-0 rounded-[13%] bg-[#1C1C1E] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.55),inset_0_0_0_2px_rgba(255,255,255,0.12)]" />
        <div className="absolute inset-[3%] rounded-[11%] overflow-hidden bg-[#0A0A0B]">
          <DashboardScreen key={cycle} cycle={cycle} />
        </div>
        <div className="absolute top-[3.2%] left-1/2 -translate-x-1/2 w-[30%] h-[3.4%] rounded-full bg-[#0A0A0B] border border-white/5" />
        <div className="absolute -left-[1.5%] top-[22%] w-[1.5%] h-[6%] rounded-l bg-[#2C2C2E]" />
        <div className="absolute -left-[1.5%] top-[31%] w-[1.5%] h-[10%] rounded-l bg-[#2C2C2E]" />
        <div className="absolute -right-[1.5%] top-[26%] w-[1.5%] h-[14%] rounded-r bg-[#2C2C2E]" />
      </motion.div>
    </div>
  );
}

function StoreBadge({ icon, top, name, testId }) {
  return (
    <div className="inline-flex items-center gap-3 rounded-xl bg-[#0A0A0A] text-white px-4 py-2.5 border border-white/10 min-w-[170px]" data-testid={testId}>
      {icon}
      <div className="leading-tight text-left"><div className="text-[9px] uppercase tracking-wider text-white/60">{top}</div><div className="text-sm font-semibold">{name}</div></div>
    </div>
  );
}

export default function MobileAppSection({ block }) {
  const features = block?.features || [];
  return (
    <section className="section-elev py-20 md:py-32 relative overflow-hidden" data-testid="mobile-app-section">
      <div className="gold-glow" style={{ width: 600, height: 600, top: -160, right: -180, opacity: 0.5 }} />
      <div className="max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <Reveal><span className="eyebrow">{block?.eyebrow || "Próximamente · App móvil"}</span></Reveal>
            <Reveal delay={120}>
              <h2 className="mt-4 font-display text-4xl md:text-5xl text-[#0A0A0A]">{renderTitle(block?.title || "SocioRubí Dashboard en tu <em>bolsillo</em>.")}</h2>
            </Reveal>
            {block?.body && <Reveal delay={220}><p className="mt-5 text-[#3F3F46] text-base leading-relaxed">{block.body}</p></Reveal>}
            {features.length > 0 && (
              <ul className="mt-8 space-y-3">
                {features.map((f, i) => (
                  <Reveal key={i} delay={280 + i * 60}>
                    <li className="flex items-start gap-3 text-sm text-[#0A0A0A]"><CheckCircle2 size={16} className="text-[#FFBF57] shrink-0 mt-0.5" /><span>{f}</span></li>
                  </Reveal>
                ))}
              </ul>
            )}
            <Reveal delay={520}>
              <div className="mt-10">
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#52525B] mb-3">Próximamente disponible para descargar en</div>
                <div className="flex flex-wrap gap-3">
                  <StoreBadge testId="badge-appstore" top="Próximamente en" name="App Store" icon={<Apple size={22} className="text-white" />} />
                  <StoreBadge testId="badge-playstore" top="Próximamente en" name="Google Play" icon={<Play size={20} className="text-[#FFBF57] fill-[#FFBF57]" />} />
                </div>
                {block?.available_at && <div className="mt-4 text-xs text-[#52525B]">Lanzamiento estimado · <span className="text-[#0A0A0A] font-medium">{block.available_at}</span></div>}
              </div>
            </Reveal>
          </div>
          <Reveal delay={200} scale><PhoneInHand /></Reveal>
        </div>
      </div>
    </section>
  );
}
